import { marketingAdmin, marketingShortLinkUrl } from './marketingAdminService';

const CODE_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

export const APP_LINK_DEFAULTS = {
  ios_scheme_url: 'com.roadserve.customer://',
  ios_fallback_url: 'https://app.roadserve.in/referral',
  android_url: 'https://play.google.com/store/apps/details?id=com.roadserve.carcare',
  android_scheme_url: '',
  android_package: 'com.roadserve.carcare',
  desktop_url: 'https://app.roadserve.in/referral',
};

export { marketingShortLinkUrl };

function normalizeCode(code) {
  return String(code || '').trim().toLowerCase();
}

export function validateLinkCode(code) {
  const normalized = normalizeCode(code);
  if (!normalized) return 'Code is required';
  if (normalized.length < 3 || normalized.length > 32) {
    return 'Code must be 3–32 characters';
  }
  if (!/^[a-z0-9-]+$/.test(normalized)) {
    return 'Code may only contain lowercase letters, numbers, and hyphens';
  }
  return null;
}

function generateRandomCode(length = 6) {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return out;
}

async function isCodeTaken(code, excludeId = null) {
  let query = marketingAdmin.from('short_links').select('id').eq('code', normalizeCode(code)).limit(1);
  if (excludeId) {
    query = query.neq('id', excludeId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).length > 0;
}

async function generateUniqueCode() {
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const code = generateRandomCode(6);
    const taken = await isCodeTaken(code);
    if (!taken) return code;
  }
  throw new Error('Could not generate a unique code. Try again.');
}

function buildLinkPayload(input, { includeCode = true } = {}) {
  const type = input.type === 'promo' ? 'promo' : 'app';
  const payload = {
    title: String(input.title || '').trim(),
    type,
    is_active: input.is_active !== false,
    utm_source: input.utm_source?.trim() || null,
    utm_medium: input.utm_medium?.trim() || null,
    utm_campaign: input.utm_campaign?.trim() || null,
    created_by: input.created_by?.trim() || null,
  };

  if (includeCode && input.code) {
    payload.code = normalizeCode(input.code);
  }

  if (type === 'promo') {
    payload.default_url = String(input.default_url || '').trim() || null;
    payload.ios_scheme_url = null;
    payload.ios_fallback_url = null;
    payload.android_url = null;
    payload.android_scheme_url = null;
    payload.android_package = null;
    payload.desktop_url = null;
  } else {
    payload.ios_scheme_url = input.ios_scheme_url?.trim() || APP_LINK_DEFAULTS.ios_scheme_url;
    payload.ios_fallback_url = input.ios_fallback_url?.trim() || APP_LINK_DEFAULTS.ios_fallback_url;
    payload.android_url = input.android_url?.trim() || APP_LINK_DEFAULTS.android_url;
    payload.android_scheme_url = input.android_scheme_url?.trim() || null;
    payload.android_package = input.android_package?.trim() || APP_LINK_DEFAULTS.android_package;
    payload.desktop_url = input.desktop_url?.trim() || APP_LINK_DEFAULTS.desktop_url;
    payload.default_url = null;
  }

  return payload;
}

export async function listLinks() {
  const { data, error } = await marketingAdmin
    .from('short_links')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);

  if (error) {
    const msg = String(error.message || '');
    if (/schema cache|does not exist|could not find the table/i.test(msg)) {
      throw new Error(
        'short_links table missing. Run supabase/security-short-links-staff.sql in Supabase SQL Editor.',
      );
    }
    if (/permission denied|row-level security|42501|not allowed/i.test(msg)) {
      throw new Error(
        'No permission to read short links. Run supabase/security-short-links-staff.sql, then log out and log in again.',
      );
    }
    throw error;
  }
  return data || [];
}

export async function createLink(input) {
  if (!input.title?.trim()) {
    throw new Error('Title is required');
  }

  let code = normalizeCode(input.code);
  if (code) {
    const codeError = validateLinkCode(code);
    if (codeError) throw new Error(codeError);
    if (await isCodeTaken(code)) throw new Error('This code is already in use');
  } else {
    code = await generateUniqueCode();
  }

  const payload = buildLinkPayload({ ...input, code }, { includeCode: true });
  const { data, error } = await marketingAdmin
    .from('short_links')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function updateLink(id, input) {
  if (!id) throw new Error('Link id is required');
  if (!input.title?.trim()) {
    throw new Error('Title is required');
  }

  const payload = buildLinkPayload(input, { includeCode: false });

  if (input.code) {
    const code = normalizeCode(input.code);
    const codeError = validateLinkCode(code);
    if (codeError) throw new Error(codeError);
    if (await isCodeTaken(code, id)) throw new Error('This code is already in use');
    payload.code = code;
  }

  const { data, error } = await marketingAdmin
    .from('short_links')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function toggleLinkActive(id, isActive) {
  const { data, error } = await marketingAdmin
    .from('short_links')
    .update({ is_active: Boolean(isActive) })
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function deleteLink(id) {
  const { error } = await marketingAdmin.from('short_links').delete().eq('id', id);
  if (error) throw error;
}

/** Match marketing-site deviceDetect — preview bots vs real devices. */
function classifyClickDevice(userAgent, storedOs) {
  const ua = String(userAgent || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  const isPreview =
    !!ua.trim()
    && !/iphone|ipad|ipod|android/.test(ua)
    && /whatsapp\/|facebookexternalhit|facebot|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|bingpreview|googlebot|yandex|baiduspider|duckduckbot|applebot|semrushbot|ahrefsbot|petalbot|bytespider/.test(ua);
  if (isPreview) return 'preview';
  if (/windows|macintosh|linux|cros/.test(ua)) return 'desktop';
  const os = String(storedOs || '').toLowerCase();
  if (os === 'ios' || os === 'android' || os === 'desktop') return os;
  return 'unknown';
}

async function fetchAllLinkClicks(linkId) {
  const pageSize = 1000;
  const all = [];

  for (let from = 0; ; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await marketingAdmin
      .from('link_clicks')
      .select('id, os, occurred_at, resolved_target, referrer, user_agent, utm_source, utm_medium, utm_campaign')
      .eq('link_id', linkId)
      .order('occurred_at', { ascending: false })
      .range(from, to);

    if (error) throw error;

    const batch = data || [];
    all.push(...batch);
    if (batch.length < pageSize) break;
  }

  return all;
}

export async function getLinkAnalytics(linkId) {
  const clicks = await fetchAllLinkClicks(linkId);

  const rows = clicks.map((row) => ({
    ...row,
    device: classifyClickDevice(row.user_agent, row.os),
  }));

  const byOs = { ios: 0, android: 0, desktop: 0, preview: 0, unknown: 0 };
  const byDayMap = {};

  rows.forEach((row) => {
    const key = byOs[row.device] !== undefined ? row.device : 'unknown';
    byOs[key] += 1;

    const day = row.occurred_at ? row.occurred_at.slice(0, 10) : 'unknown';
    byDayMap[day] = (byDayMap[day] || 0) + 1;
  });

  const byDay = Object.entries(byDayMap)
    .map(([day, count]) => ({ day, count }))
    .sort((a, b) => b.day.localeCompare(a.day));

  const preview = byOs.preview || 0;
  const human = Math.max(rows.length - preview, 0);

  return {
    total: rows.length,
    human,
    preview,
    byOs,
    byDay,
    recent: rows.slice(0, 25),
  };
}

export function emptyLinkForm(type = 'app') {
  return {
    title: '',
    code: '',
    type,
    is_active: true,
    default_url: '',
    ios_scheme_url: APP_LINK_DEFAULTS.ios_scheme_url,
    ios_fallback_url: APP_LINK_DEFAULTS.ios_fallback_url,
    android_url: APP_LINK_DEFAULTS.android_url,
    android_scheme_url: APP_LINK_DEFAULTS.android_scheme_url,
    android_package: APP_LINK_DEFAULTS.android_package,
    desktop_url: APP_LINK_DEFAULTS.desktop_url,
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
  };
}

export function linkToForm(link) {
  return {
    title: link.title || '',
    code: link.code || '',
    type: link.type || 'app',
    is_active: link.is_active !== false,
    default_url: link.default_url || '',
    ios_scheme_url: link.ios_scheme_url || APP_LINK_DEFAULTS.ios_scheme_url,
    ios_fallback_url: link.ios_fallback_url || APP_LINK_DEFAULTS.ios_fallback_url,
    android_url: link.android_url || APP_LINK_DEFAULTS.android_url,
    android_scheme_url: link.android_scheme_url || '',
    android_package: link.android_package || APP_LINK_DEFAULTS.android_package,
    desktop_url: link.desktop_url || APP_LINK_DEFAULTS.desktop_url,
    utm_source: link.utm_source || '',
    utm_medium: link.utm_medium || '',
    utm_campaign: link.utm_campaign || '',
  };
}
